import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createDocument } from './createDocument';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import Modal from '../../components/Modal';
import SearchableProjectDropdown from '../../components/SearchableProjectDropdown';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import { DATE_RANGE_PRESETS, getInvoiceDateRange } from '../InvoiceRegister/config';
import { ColumnDef } from '@tanstack/react-table';
import {
  DocumentListItem,
  DocumentTemplate,
  DocumentChromeVariant,
  DocumentStatus,
  DEFAULT_DOCUMENT_TITLES,
  formatDate,
  statusLabel,
  statusTone
} from './config';
import StatusPill from '../../components/StatusPill';
import TableSkeleton from '../../components/TableSkeleton';
import TextInput from '../../components/TextInput';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import DocumentEditor from './components/DocumentEditor';
import { DocumentNavTab } from './components/DocumentEditor/DocumentNav';
import { useQueryClient } from '@tanstack/react-query';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, DOCUMENT_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import './styles.scss';

// =======================================
// INTERFACES
// =======================================

interface LocationState {
  openCreateModal?: boolean;
  preselectedProjectId?: number;
  preselectedStageId?: number;
}

interface TeamMemberOption {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

/** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
};

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * A template the current user may create against a particular job, as returned
 * by get_available_document_templates. Narrower than DocumentTemplate: the RPC
 * returns only what a create flow needs, and it is the RPC -- not this file --
 * that decides which templates a job may have.
 */
interface AvailableTemplate {
  id: string;
  code: string | null;
  name: string;
  document_type: string;
  category: string;
  display_order: number;
  numbering_pattern: string;
  signature_slots: { key: string; label: string; signer: string; required?: boolean }[];
  chrome: DocumentChromeVariant;
  creates_inspection: boolean;
}

const Documents: React.FC = () => {
  const { documentId } = useParams<{ documentId?: string }>();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const session = useSession();
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Static data state
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [userType, setUserType] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  // Read-only QA auditor — derived from the same role query below (no extra fetch).
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [currentUserTeamMemberId, setCurrentUserTeamMemberId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([]);
  const [staticDataLoading, setStaticDataLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState<{ all: number; draft: number; awaiting: number; signed: number; sent: number }>({ all: 0, draft: 0, awaiting: 0, signed: 0, sent: 0 });

  // Track if default createdBy filter has been initialized
  const [defaultCreatedByInitialized, setDefaultCreatedByInitialized] = useState(false);

  /**
   * The editor's left panel, held here because the editor is keyed on
   * documentId: state inside it would reset on every sibling click, collapsing
   * the panel the moment it was used. Starts collapsed -- expanded it leaves
   * less room than the A4 page needs, so the canvas would have to scroll before
   * the user has asked for anything.
   */
  const [navCollapsed, setNavCollapsed] = useState(true);
  const [navTab, setNavTab] = useState<DocumentNavTab>('documents');
  /**
   * The job the open document belongs to, so the panel can stay painted while
   * the next one loads (the editor unmounts, this does not).
   *
   * Cleared on the way back to the register: siblings always share a job, so
   * carrying it between them is safe, but the NEXT document opened from the
   * register may be on a different one -- and a panel showing the previous
   * job's documents for a moment is worse than showing none.
   */
  const [navScope, setNavScope] = useState<{ projectId: number | null; stageId: number | null } | null>(null);

  useEffect(() => {
    if (!documentId) setNavScope(null);
  }, [documentId]);

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  /**
   * Templates valid for the job chosen in the create modal, tagged with the job
   * they were fetched for.
   *
   * One piece of state, not a list plus a loading flag: two would be set in
   * separate updates, and for the frame between selecting a job and the effect
   * running they said "loaded, and empty" -- so "No document types are
   * available" flashed under the dropdown every time a job was picked.
   */
  const [templatesFor, setTemplatesFor] =
    useState<{ jobKey: string; list: AvailableTemplate[] } | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedStage, setSelectedStage] = useState<{ id: number; code: string; description: string } | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // =======================================
  // URL FILTERS
  // =======================================

  const {
    filters: urlFilters,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters
  } = useUrlFilters(DOCUMENT_FILTER_DEFINITIONS);

  // =======================================
  // SERVER FILTERING
  // =======================================

  const {
    data: documents,
    pagination,
    loading: documentsLoading,
    filtering: documentsFiltering,
    setFilters: setServerFilters
  } = useServerFiltering<DocumentListItem>({
    rpcFunction: 'get_filtered_documents',
    defaultFilters: {
      search_term: null,
      status_filter: null,
      type_filter: null,
      project_filter: null,
      client_filter: null,
      created_by_filter: null,
      from_date: null,
      to_date: null,
      page_size: 25,
      page_offset: 0,
      sort_by: 'document_date',
      sort_order: 'desc'
    },
    transformData: (data) => data.map((doc: any) => ({
      id: doc.id,
      template_id: doc.template_id,
      template_name: doc.template_name || '',
      document_type: doc.document_type || '',
      project_id: doc.project_id,
      stage_id: doc.stage_id,
      stage_code: doc.stage_code || null,
      stage_description: doc.stage_description || null,
      job_number: doc.job_number || null,
      project_description: doc.project_description || null,
      client_name: doc.client_name || null,
      document_number: doc.document_number,
      title: doc.title,
      status: doc.status,
      signatory_name: doc.signatory 
        ? `${doc.signatory.first_name} ${doc.signatory.last_name}`
        : null,
      created_by_name: doc.creator
        ? `${doc.creator.first_name} ${doc.creator.last_name}`
        : null,
      document_date: doc.document_date,
      created_at: doc.created_at,
      updated_at: doc.updated_at
    }))
  });

  // Sync URL filters to server filters
  // Date presets resolve client-side to concrete dates, same as Fees/Invoices.
  // fromDate/toDate in the URL only carry a custom range.
  const dateRangePreset = urlFilters.dateRange || 'allTime';
  const customDateRange = dateRangePreset === 'custom';

  const { fromDate, toDate } = useMemo(() => {
    if (customDateRange) {
      return { fromDate: urlFilters.fromDate, toDate: urlFilters.toDate };
    }
    const range = getInvoiceDateRange(dateRangePreset);
    return { fromDate: range.from, toDate: range.to };
  }, [customDateRange, urlFilters.fromDate, urlFilters.toDate, dateRangePreset]);

  useEffect(() => {
    if (!defaultCreatedByInitialized) return;

    const page = urlFilters.page || 1;
    const pageSize = urlFilters.pageSize || 25;
    // Non-managers always filter by own ID; managers can use URL filter
    const createdByFilter = isManager
      ? (urlFilters.createdBy || null)
      : (currentUserTeamMemberId || null);

    setServerFilters({
      search_term: urlFilters.search || null,
      status_filter: urlFilters.status.length > 0 ? urlFilters.status : null,
      type_filter: urlFilters.type || null,
      // The project filter was removed from the register: search_text already
      // covers job number, project and stage description, so search finds them.
      project_filter: null,
      client_filter: urlFilters.client ? parseInt(urlFilters.client, 10) : null,
      created_by_filter: createdByFilter,
      from_date: fromDate || null,
      to_date: toDate || null,
      page_size: pageSize,
      page_offset: (page - 1) * pageSize
    });
  }, [urlFilters, setServerFilters, defaultCreatedByInitialized, currentUserTeamMemberId, isManager, fromDate, toDate]);

  // A stable primitive summary of the current page's rows. useServerFiltering
  // returns a brand-new [] on every render while the query has no data (initial
  // load, or a query error), so depending on `documents` itself would re-fire
  // the counts effect on every render -- and because the effect setStates a new
  // object, that is an unbounded RPC loop. A string compares by value, so the
  // effect only re-runs when a row is actually added, removed or restatused.
  const documentsVersion = useMemo(
    () => documents.map(d => `${d.id}:${d.status}`).join(','),
    [documents]
  );

  // Fetch the status-pipeline counts (mirrors the register's visibility and its
  // current filters, minus status, so the badges reflect the current view).
  useEffect(() => {
    if (!defaultCreatedByInitialized) return;
    let cancelled = false;

    const createdByFilter = isManager
      ? (urlFilters.createdBy || null)
      : (currentUserTeamMemberId || null);

    supabase.rpc('get_document_status_counts', {
      search_term: urlFilters.search || null,
      type_filter: urlFilters.type || null,
      project_filter: null,
      client_filter: urlFilters.client ? parseInt(urlFilters.client, 10) : null,
      created_by_filter: createdByFilter,
      from_date: fromDate || null,
      to_date: toDate || null,
    }).then(({ data, error }) => {
      if (cancelled || error || !data) return;
      setStatusCounts({
        all: data.all ?? 0,
        draft: data.draft ?? 0,
        awaiting: data.awaiting ?? 0,
        signed: data.signed ?? 0,
        sent: data.sent ?? 0,
      });
    });

    return () => { cancelled = true; };
  }, [
    defaultCreatedByInitialized, isManager, currentUserTeamMemberId, supabase,
    urlFilters.search, urlFilters.type, urlFilters.client,
    urlFilters.createdBy, fromDate, toDate,
    // Re-count after the list changes (a create/sign/delete shifts the buckets).
    documentsVersion,
  ]);

  // Handle location state for opening create modal with preselected project/stage
  useEffect(() => {
    if (locationState?.openCreateModal && !documentId) {
      setIsCreateModalOpen(true);
      if (locationState.preselectedProjectId) {
        setSelectedProjectId(locationState.preselectedProjectId);
      }
      if (locationState.preselectedStageId) {
        const stageId = locationState.preselectedStageId;
        supabase
          .from('project_stages')
          .select('id, stage_id, stage_description')
          .eq('id', stageId)
          .single()
          .then(({ data, error }) => {
            if (!error && data) {
              setSelectedStage({
                id: data.id,
                code: data.stage_id,
                description: data.stage_description || ''
              });
            }
          });
      }
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [locationState, navigate, location.pathname, documentId, supabase]);

  // =======================================
  // DATA FETCHING
  // =======================================

  // Fetch user type, team member ID, and static data (runs once on first list view)
  useEffect(() => {
    if (documentId) return;
    if (defaultCreatedByInitialized) return;

    const fetchStaticData = async () => {
      if (!session?.user) return;

      try {
        setStaticDataLoading(true);

        // Invalidate any stale document query cache so fresh data loads
        queryClient.invalidateQueries({ queryKey: ['get_filtered_documents'] });

        // Get user's team member info
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select(`
            team_member_id,
            team_members (
              type,
              role
            )
          `)
          .eq('id', session.user.id)
          .single();

        let userIsManager = false;
        if (profileError) {
          console.error('Error fetching profile:', profileError);
        } else if (profileData) {
          const teamMemberData = profileData.team_members as unknown as { type: string; role: string } | null;
          setUserType(teamMemberData?.type || null);
          // QA auditors get the same visibility as managers (e.g. the "Created By"
          // filter / seeing all documents); read-only is enforced separately.
          userIsManager = teamMemberData?.role === 'manager' || teamMemberData?.role === 'qa';
          setIsManager(userIsManager);
          setIsReadOnly(teamMemberData?.role === 'qa');
          setCurrentUserTeamMemberId(profileData.team_member_id || null);
        }

        // Fetch templates
        const { data: templatesData, error: templatesError } = await supabase
          .from('document_templates')
          .select('*')
          .eq('is_active', true)
          .order('name');

        if (templatesError) {
          console.error('Error fetching templates:', templatesError);
        } else {
          setTemplates(templatesData || []);
        }

        // Only fetch team members for "Created By" filter if manager
        if (userIsManager) {
          const { data: teamMembersData, error: teamMembersError } = await supabase
            .rpc('search_team_members', { search_term: null, limit_results: 100 });

          if (teamMembersError) {
            console.error('Error fetching team members:', teamMembersError);
          } else {
            setTeamMembers(teamMembersData || []);
          }
        }

        // Set initial server filters using local variables (avoids state-timing issues)
        const teamMemberId = profileData?.team_member_id || null;
        const initialCreatedBy = userIsManager
          ? (urlFilters.createdBy || null)
          : (teamMemberId || null);

        setServerFilters({
          search_term: urlFilters.search || null,
          status_filter: urlFilters.status.length > 0 ? urlFilters.status : null,
          type_filter: urlFilters.type || null,
          project_filter: urlFilters.project ? parseInt(urlFilters.project, 10) : null,
          client_filter: urlFilters.client ? parseInt(urlFilters.client, 10) : null,
          created_by_filter: initialCreatedBy,
          from_date: urlFilters.fromDate || null,
          to_date: urlFilters.toDate || null,
          page_size: urlFilters.pageSize || 25,
          page_offset: 0
        });
        setDefaultCreatedByInitialized(true);

      } catch (error) {
        console.error('Error fetching static data:', error);
      } finally {
        setStaticDataLoading(false);
      }
    };

    fetchStaticData();
  }, [session, supabase, documentId, defaultCreatedByInitialized, urlFilters, setServerFilters, queryClient]);

  // =======================================
  // HANDLERS
  // =======================================

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const isNarrow = useNarrowViewport();

  // The register's status pipeline: All / Drafts / Request Signature / Signed /
  // Sent, driven by the same status_filter the register already uses. Signed is
  // the "still to send" list -- the reason Signed and Sent are separate statuses.
  const STATUS_BUCKETS = useMemo(() => ([
    { value: 'all', label: 'All', statuses: [] as string[], countKey: 'all' as const },
    { value: 'draft', label: 'Drafts', statuses: ['Draft'], countKey: 'draft' as const },
    { value: 'awaiting', label: 'Request Signature', statuses: ['Awaiting Approval'], countKey: 'awaiting' as const },
    { value: 'signed', label: 'Signed', statuses: ['Signed'], countKey: 'signed' as const },
    { value: 'sent', label: 'Sent', statuses: ['Sent'], countKey: 'sent' as const },
  ]), []);

  const activeBucket = useMemo(() => {
    const s = urlFilters.status;
    if (s.length === 1) {
      const match = STATUS_BUCKETS.find(b => b.statuses.length === 1 && b.statuses[0] === s[0]);
      if (match) return match.value;
    }
    return 'all';
  }, [urlFilters.status, STATUS_BUCKETS]);

  const handleBucketChange = useCallback((bucketValue: string) => {
    const bucket = STATUS_BUCKETS.find(b => b.value === bucketValue);
    setUrlFilters({ status: bucket ? bucket.statuses : [], page: 1 });
  }, [STATUS_BUCKETS, setUrlFilters]);

  const handleTypeChange = useCallback((type: string) => {
    setUrlFilters({ type: type || '', page: 1 });
  }, [setUrlFilters]);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    setUrlFilters({ client: clientId ? String(clientId) : '', page: 1 });
  }, [setUrlFilters]);

  const handleCreatedByChange = useCallback((createdBy: string) => {
    setUrlFilters({ createdBy: createdBy || '', page: 1 });
  }, [setUrlFilters]);

  const handleDateRangeChange = useCallback((v: string | string[]) => {
    const next = Array.isArray(v) ? v[0] ?? '' : v;
    if (next === 'custom') {
      // Seed the custom inputs with the current month so the range is valid the
      // moment it is shown (same as the Fees register).
      const seed = getInvoiceDateRange('thisMonth');
      setUrlFilters({ dateRange: next, fromDate: seed.from, toDate: seed.to, page: 1 });
    } else {
      setUrlFilters({ dateRange: next, fromDate: '', toDate: '', page: 1 });
    }
  }, [setUrlFilters]);

  const handleFromDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ fromDate: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleToDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ toDate: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
    setServerFilters({
      search_term: null,
      status_filter: null,
      type_filter: null,
      project_filter: null,
      client_filter: null,
      created_by_filter: isManager ? null : (currentUserTeamMemberId || null),
      from_date: null,
      to_date: null,
      page_size: 25,
      page_offset: 0
    });
  }, [resetUrlFilters, currentUserTeamMemberId, setServerFilters, isManager]);

  const handleRowClick = useCallback((document: DocumentListItem) => {
    navigate(`/documents/${document.id}`);
  }, [navigate]);

  const handlePageChange = useCallback((page: number) => {
    setUrlFilters({ page });
  }, [setUrlFilters]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  // =======================================
  // CREATE DOCUMENT HANDLERS
  // =======================================

  const handleOpenCreateModal = useCallback(() => {
    // No template is preselected: which ones exist depends on the job, and the
    // job has not been chosen yet.
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setSelectedTemplateId('');
    setSelectedProjectId(null);
    setSelectedStage(null);
    setDocumentTitle('');
    setTemplatesFor(null);
    setCreateError(null);
  }, []);

  // Identifies the job the template list belongs to, so a list fetched for the
  // previous job is never shown as this one's.
  const jobKey = selectedProjectId ? `${selectedProjectId}:${selectedStage?.id ?? ''}` : '';
  // Memoised for its IDENTITY, not its cost: written inline, the mismatch branch
  // allocated a fresh [] on every render, so everything downstream that lists it
  // as a dependency re-ran on every render -- including the effect that clears an
  // invalidated template. (CI builds with CI=true, which makes the
  // react-hooks/exhaustive-deps warning about this a build failure.)
  const availableTemplates = useMemo(
    () => (templatesFor?.jobKey === jobKey ? templatesFor.list : []),
    [templatesFor, jobKey]
  );
  const isLoadingTemplates = !!jobKey && templatesFor?.jobKey !== jobKey;

  /**
   * The templates that may be created against the chosen job.
   *
   * Read from get_available_document_templates rather than filtered here: it
   * already applies every rule -- the role's can_create, applies_to_job_types,
   * and whether the template belongs on a project or a stage -- and the
   * project/stage Documents tab offers templates the same way. Filtering
   * locally would be a second copy of those rules, free to drift from the one
   * create_document enforces.
   */
  useEffect(() => {
    if (!isCreateModalOpen || !selectedProjectId) {
      setTemplatesFor(null);
      return;
    }
    let cancelled = false;
    supabase
      .rpc('get_available_document_templates', {
        p_project_id: selectedProjectId,
        p_stage_id: selectedStage?.id ?? null,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Error loading templates for job:', error);
        setTemplatesFor({ jobKey, list: error ? [] : ((data as AvailableTemplate[]) || []) });
      });
    return () => { cancelled = true; };
    // jobKey is derived from the two ids below; listing it as well would only
    // re-run this for the same job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateModalOpen, selectedProjectId, selectedStage, supabase]);

  // Choosing a job can invalidate the template already picked -- switching from
  // an RWD stage to a Design project takes the design certificate away.
  useEffect(() => {
    if (selectedTemplateId && !availableTemplates.some(t => t.id === selectedTemplateId)) {
      setSelectedTemplateId('');
      setDocumentTitle('');
    }
  }, [availableTemplates, selectedTemplateId]);

  const handleCreateDocument = useCallback(async () => {
    if (!session?.user || !selectedTemplateId || !selectedProjectId) {
      setCreateError('Please select a template and project');
      return;
    }

    try {
      setIsCreating(true);
      setCreateError(null);

      const template = availableTemplates.find(t => t.id === selectedTemplateId);
      if (!template) {
        setCreateError('Template not found');
        return;
      }

      const newDocumentId = await createDocument(supabase, session.user.id, {
        templateId: selectedTemplateId,
        projectId: selectedProjectId,
        stageId: selectedStage?.id ?? null,
        title: documentTitle,
        chrome: template.chrome,
        templateCode: template.code,
        requiresCertifiedSignatory: (template.signature_slots || []).some((sl: { signer: string }) => sl.signer === 'certified'),
      });

      // The register's rows are a react-query cache that is fresh for two
      // minutes and outlives the editor, and the editor's autosave only
      // patches rows already in it. Without this the document just created
      // was missing from the list on closing the editor until a reload. The
      // builder's left panel caches this job's documents the same way.
      void queryClient.invalidateQueries({ queryKey: ['get_filtered_documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document_siblings', selectedProjectId, selectedStage?.id ?? null] });

      handleCloseCreateModal();
      navigate(`/documents/${newDocumentId}`);

    } catch (error) {
      console.error('Error creating document:', error);
      setCreateError((error as Error)?.message || 'An unexpected error occurred');
    } finally {
      setIsCreating(false);
    }
  }, [session, supabase, queryClient, selectedTemplateId, selectedProjectId, selectedStage, documentTitle, availableTemplates, navigate, handleCloseCreateModal]);

  // =======================================
  // COMPUTED VALUES
  // =======================================

  // Check if filters differ from default (for a manager, all authors)
  const hasActiveFilters = useMemo(() => {
    const hasSearch = urlFilters.search.length > 0;
    const hasStatus = urlFilters.status.length > 0;
    const hasType = urlFilters.type.length > 0;
    const hasClient = urlFilters.client.length > 0;
    const hasDateRange = (urlFilters.dateRange || 'allTime') !== 'allTime';
    // Only managers can change createdBy, so only count it as active for them.
    // Any author narrows a manager's default (everyone), themselves included.
    const hasCreatedBy = isManager && urlFilters.createdBy.length > 0;

    return hasSearch || hasStatus || hasType || hasClient || hasDateRange || hasCreatedBy;
  }, [urlFilters, isManager]);

  const totalRecords = pagination?.total_records || 0;
  const totalPages = pagination?.total_pages || 1;
  const currentPage = urlFilters.page || 1;
  const pageSize = urlFilters.pageSize || 25;

  const creatableTemplates = useMemo(() => {
    if (!userType) return [];
    return templates.filter(t => t.can_create.includes(userType));
  }, [templates, userType]);

  const canCreateDocuments = creatableTemplates.length > 0;

  // Dropdown items
  const dateRangeItems: DropdownItem[] = useMemo(
    () => DATE_RANGE_PRESETS.map(p => ({ id: p.value, label: p.label, value: p.value })),
    []
  );

  const typeDropdownItems: DropdownItem[] = useMemo(() => 
    templates.map(t => ({
      id: t.document_type,
      label: t.name,
      value: t.document_type
    })).filter((item, index, self) => 
      self.findIndex(i => i.id === item.id) === index
    ), [templates]
  );

  const templateDropdownItems: DropdownItem[] = useMemo(() =>
    availableTemplates.map(t => ({
      id: t.id,
      label: t.name,
      value: t.id
    })), [availableTemplates]
  );

  const createdByDropdownItems: DropdownItem[] = useMemo(() => [
    { id: 'all', label: 'All Team Members', value: '' },
    ...teamMembers.map(tm => ({
      id: tm.id,
      label: tm.full_name,
      value: tm.id
    }))
  ], [teamMembers]);

  // The Author dropdown shows exactly what the register is filtered by: empty
  // is "All Team Members", which is what a manager sees by default (the RPC
  // gets created_by_filter null). It must never fall back to the current user
  // for display -- that showed the manager as selected while listing everyone's
  // documents, and picking themselves was then a no-op, since the dropdown
  // already held that value and the URL never changed.
  const currentCreatedByValue = urlFilters.createdBy;

  // =======================================
  // TABLE COLUMNS
  // =======================================

  const columns = useMemo<ColumnDef<DocumentListItem>[]>(() => [
    // Column budget (table-layout: fixed on desktop). The five fixed columns
    // take ~610px; Title and Project share what is left, Title first: Project
    // is a percentage of the table and Title is the one `auto` column, so the
    // slack goes to Title after Project has its share. At 1296px that is
    // roughly Title 400 / Project 285; at 1136px, 276 / 250. Both carry a
    // hover tooltip because either can still truncate on a narrow screen.
    {
      accessorKey: 'job_number',
      header: 'Job #',
      // 24px left gutter + 16px right + the sort-indicator slot: under ~96px
      // the header itself ellipsises.
      meta: { minWidth: '96px', width: '96px' },
      cell: ({ row }) => row.original.job_number || '—'
    },
    {
      accessorKey: 'document_date',
      header: 'Date',
      meta: { minWidth: '112px', width: '112px' }, // dd/mm/yyyy + 32px cell padding
      cell: ({ row }) => formatDate(row.original.document_date || row.original.created_at)
    },
    {
      accessorKey: 'title',
      header: 'Title',
      meta: { minWidth: '220px', width: 'auto' },
      cell: ({ row }) => <span title={row.original.title || undefined}>{row.original.title || '—'}</span>
    },
    {
      accessorKey: 'project_description',
      header: 'Project',
      meta: { minWidth: '180px', width: '22%' },
      cell: ({ row }) => (
        <span title={row.original.project_description || undefined}>{row.original.project_description || '—'}</span>
      )
    },
    {
      accessorKey: 'template_name',
      header: 'Type',
      meta: { minWidth: '130px', width: '150px' },
      cell: ({ row }) => <span title={row.original.template_name || undefined}>{row.original.template_name}</span>
    },
    {
      accessorKey: 'status',
      header: 'Status',
      // Wide enough for "Awaiting Approval", the longest status. The table is
      // table-layout: fixed on desktop and nothing clips a cell, so a pill
      // wider than its column does not truncate -- it draws straight over the
      // next one. At 100px the old badge overlapped Created By by 78px. The
      // 11px pill measures ~130px; sized with headroom rather than to the
      // measurement: a couple of spare pixels is the same bug again the first
      // time a glyph renders wider.
      meta: { minWidth: '150px', width: '150px' },
      cell: ({ row }) => (
        <StatusPill
          label={statusLabel[row.original.status as DocumentStatus] ?? row.original.status}
          tone={statusTone[row.original.status as DocumentStatus] ?? 'neutral'}
        />
      )
    },
    {
      accessorKey: 'created_by_name',
      header: 'Author',
      meta: { minWidth: '108px', width: '108px' }, // the letter-spaced header, its sort slot and the 24px right gutter
      // An initials bubble (the form primitives' .ui-avatar) with the full
      // name on hover: the column is a glance, not a read.
      cell: ({ row }) => {
        const name = row.original.created_by_name;
        if (!name) return '—';
        return (
          <span className="ui-avatar" title={name} aria-label={name}>
            {initials(name)}
          </span>
        );
      }
    }
  ], []);

  // =======================================
  // RENDER
  // =======================================

  if (documentId) {
    return (
      <div className="document-editor-fullscreen">
        {/*
          * KEYED ON documentId, so opening another document from the editor's
          * left panel is a real unmount and remount.
          *
          * Without the key the component stays mounted and only the fetch
          * re-runs, which loses work: the 2s autosave timer stays armed, and
          * when it fires saveDocument reads documentRef.current -- by then the
          * document you switched TO. The edit you just made is dropped and a
          * redundant write lands on the new document. The unmount flush cannot
          * catch it either; its effect depends on saveDocument, whose deps do
          * not include documentId. The dirty counters, the active page and the
          * canvas scroll position leak across as well.
          *
          * The cost is one page re-measurement, which opening a document from
          * this register already pays. This does not remount Layout -- see the
          * comment on the route in App.tsx, which is about that and is
          * unaffected by keying a component below it.
          */}
        <DocumentEditor
          key={documentId}
          navCollapsed={navCollapsed}
          onNavCollapsedChange={setNavCollapsed}
          navTab={navTab}
          onNavTabChange={setNavTab}
          navScope={navScope}
          onNavScopeChange={setNavScope}
        />
      </div>
    );
  }

  return (
    <ListPage
      title="Documents"
      className="documents-page"
      loading={staticDataLoading}
      skeleton={{ segmentCount: 5, filterCount: isManager ? 4 : 3, showAction: canCreateDocuments }}
      search={
        <SearchInput
          placeholder="Search documents, projects, job number…"
          value={urlFilters.search}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search documents"
        />
      }
      actions={
        canCreateDocuments && (
          <button
            type="button"
            className="pill-btn pill-btn--solid pill-btn--sm"
            onClick={handleOpenCreateModal}
            disabled={documentsLoading || isReadOnly}
          >
            <PlusIcon /> New document
          </button>
        )
      }
      segments={
        <StatusSegments
          aria-label="Document status"
          size={isNarrow ? 'sm' : 'md'}
          value={activeBucket}
          onChange={handleBucketChange}
          segments={STATUS_BUCKETS.map((b) => ({
            value: b.value,
            label: b.label,
            count: statusCounts[b.countKey],
          }))}
        />
      }
      filters={
        <>
          {isManager && (
            <FilterTrigger
              label="Author"
              active={Boolean(currentCreatedByValue)}
            >
              <Dropdown
                items={createdByDropdownItems}
                value={currentCreatedByValue}
                onChange={handleCreatedByChange}
                placeholder="Anyone"
                searchable
              />
            </FilterTrigger>
          )}

          <FilterTrigger label="Type" active={urlFilters.type.length > 0}>
            <Dropdown
              items={typeDropdownItems}
              value={urlFilters.type}
              onChange={handleTypeChange}
              placeholder="All"
            />
          </FilterTrigger>

          <FilterTrigger label="Client" active={urlFilters.client.length > 0}>
            <SearchableClientDropdown
              value={urlFilters.client ? parseInt(urlFilters.client, 10) : null}
              onChange={handleClientChange}
              placeholder="All"
            />
          </FilterTrigger>

          <FilterTrigger label="Date" active={dateRangePreset !== 'allTime'}>
            <Dropdown
              items={dateRangeItems}
              value={dateRangePreset}
              onChange={handleDateRangeChange}
              placeholder="All time"
              searchable={false}
            />
          </FilterTrigger>

          {customDateRange && (
            <>
              <FilterTrigger label="From" active>
                <TextInput
                  size="sm"
                  type="date"
                  value={urlFilters.fromDate}
                  onChange={handleFromDateChange}
                  aria-label="From date"
                />
              </FilterTrigger>
              <FilterTrigger label="To" active>
                <TextInput
                  size="sm"
                  type="date"
                  value={urlFilters.toDate}
                  onChange={handleToDateChange}
                  aria-label="To date"
                />
              </FilterTrigger>
            </>
          )}
        </>
      }
      hasActiveFilters={Boolean(hasActiveFilters)}
      onClearFilters={handleClearFilters}
      resultsText={
        !documentsLoading && (
          <>
            Showing {documents.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0}–{Math.min(currentPage * pageSize, totalRecords)} of {totalRecords.toLocaleString()} documents
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next || false}
          hasPrevious={pagination?.has_previous || false}
          loading={documentsLoading || documentsFiltering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {staticDataLoading || documentsLoading ? (
        <TableSkeleton compact rows={14} columns={7} showHeader />
      ) : documents.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters
            ? 'No documents match these filters.'
            : 'No documents yet. Create your first document to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${documentsFiltering ? 'is-filtering' : ''}`.trim()}>
          <Table
            data={documents}
            columns={columns}
            onRowClick={(row) => handleRowClick(row)}
            stickyHeader
          />
        </div>
      )}

      {/* Create Document Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title="New Document"
        size="md"
        hasFooter={true}
        footerContent={
          <>
            <button 
              className="btn-secondary" 
              onClick={handleCloseCreateModal}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleCreateDocument}
              disabled={isCreating || !selectedTemplateId || !selectedProjectId}
            >
              {isCreating ? 'Creating...' : 'Create Document'}
            </button>
          </>
        }
      >
        <div className="create-document-modal">
          <div className="modal-form">
            {/* Job first. Which templates exist depends on it -- a design
                certificate is for an RWD stage and nothing else -- so asking for
                the template first meant offering ones the job cannot have. */}
            <div className="form-group">
              <label>Project or Stage *</label>
              <SearchableProjectDropdown
                value={selectedProjectId}
                onChange={(id, project) => {
                  if (id && project?.entity_type === 'stage') {
                    setSelectedProjectId(project.parent_project_id ?? null);
                    setSelectedStage({
                      id: typeof id === 'string' ? parseInt(id, 10) : (id as number),
                      code: project.stage_id ?? '',
                      description: project.stage_description ?? ''
                    });
                  } else if (id) {
                    setSelectedProjectId(typeof id === 'string' ? parseInt(id, 10) : (id as number));
                    setSelectedStage(null);
                  } else {
                    setSelectedProjectId(null);
                    setSelectedStage(null);
                  }
                }}
                placeholder="Search and select project or stage..."
              />
              {selectedStage && (
                <span className="help-text">
                  Stage {selectedStage.code}{selectedStage.description ? ` — ${selectedStage.description}` : ''}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Template *</label>
              <Dropdown
                items={templateDropdownItems}
                value={selectedTemplateId}
                onChange={(value) => {
                  const id = Array.isArray(value) ? value[0] : value;
                  setSelectedTemplateId(id);
                  // A template whose title is standardised fills it in; anything
                  // else leaves whatever the user has already typed alone.
                  const code = availableTemplates.find(t => t.id === id)?.code ?? '';
                  const standard = DEFAULT_DOCUMENT_TITLES[code];
                  if (standard) setDocumentTitle(standard);
                }}
                placeholder={selectedProjectId ? 'Select template...' : 'Choose a project or stage first'}
                disabled={!selectedProjectId || isLoadingTemplates}
              />
              {selectedProjectId && !isLoadingTemplates && availableTemplates.length === 0 && (
                <span className="help-text">
                  No document types are available for this {selectedStage ? 'stage' : 'project'}.
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Document Title</label>
              <input
                type="text"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="e.g., Foundation Inspection Report"
                disabled={!selectedTemplateId}
              />
              <span className="help-text">
                Used in document number: {selectedTemplateId && availableTemplates.find(t => t.id === selectedTemplateId)?.numbering_pattern}
              </span>
            </div>

            {createError && (
              <div className="error-text">{createError}</div>
            )}
          </div>
        </div>
      </Modal>
    </ListPage>
  );
};

export default Documents;
